// apps/web/src/components/coletor/teclado-numerico.tsx — Fase 13, Plano 30 (EST-15, D-05, D-28).
// Teclado numérico próprio do coletor: grade 3x4 de teclas de 64px (0-9, vírgula, apagar) + "Limpar". Substitui o
// teclado do sistema (que cobriria a tela do tablet). No desktop aceita o teclado físico; Enter confirma.
import { useEffect, useRef, type ReactElement } from 'react';
import { Delete } from 'lucide-react';
import { TECLAS_PESO, TECLA_APAGAR, TECLA_LIMPAR, TECLA_VIRGULA, enterConfirmaPesagem, teclaDoEventoFisico } from '../../lib/estoque/teclado-peso';

interface TecladoNumericoProps {
  readonly onTecla: (tecla: string) => void;
  /** Enter no teclado físico; o chamador decide se o registro é válido. */
  readonly onConfirmar?: () => void;
  readonly desabilitado?: boolean;
}

const CLASSE_TECLA =
  'min-h-[64px] min-w-[64px] rounded-xl border border-slate-700 bg-slate-800 text-xl font-semibold text-slate-100 touch-manipulation ' +
  'hover:bg-slate-700 active:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50';

function rotuloAria(tecla: string): string {
  if (tecla === TECLA_VIRGULA) return 'Vírgula';
  if (tecla === TECLA_APAGAR) return 'Apagar';
  if (tecla === TECLA_LIMPAR) return 'Limpar';
  return `Dígito ${tecla}`;
}

export function TecladoNumerico({ onTecla, onConfirmar, desabilitado = false }: TecladoNumericoProps): ReactElement {
  const onTeclaRef = useRef(onTecla);
  const onConfirmarRef = useRef(onConfirmar);
  onTeclaRef.current = onTecla;
  onConfirmarRef.current = onConfirmar;

  useEffect(() => {
    if (desabilitado) return undefined;
    function aoTeclar(evento: KeyboardEvent): void {
      const alvo = evento.target as HTMLElement | null;
      // Campos de texto (Detalhes, Justificativa, código digitado) ficam com o teclado normal.
      if (alvo && (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA')) return;
      if (evento.ctrlKey || evento.metaKey || evento.altKey) return;
      if (evento.key === 'Enter') {
        // Com o foco em botão, link ou seleção o Enter é do controle (CR-C02): nunca registra a pesagem por baixo.
        if (!enterConfirmaPesagem(alvo)) return;
        if (onConfirmarRef.current) {
          evento.preventDefault();
          onConfirmarRef.current();
        }
        return;
      }
      const tecla = teclaDoEventoFisico(evento.key);
      if (tecla === null) return;
      evento.preventDefault();
      onTeclaRef.current(tecla);
    }
    window.addEventListener('keydown', aoTeclar, true);
    return () => window.removeEventListener('keydown', aoTeclar, true);
  }, [desabilitado]);

  return (
    <div className="flex flex-col gap-2" role="group" aria-label="Teclado numérico">
      <div className="grid grid-cols-3 gap-2">
        {TECLAS_PESO.map((tecla) => (
          <button
            key={tecla}
            type="button"
            aria-label={rotuloAria(tecla)}
            disabled={desabilitado}
            // Mantém o foco fora da tecla: um Enter depois do toque não repete a tecla.
            onMouseDown={(evento) => evento.preventDefault()}
            onClick={() => onTecla(tecla)}
            className={CLASSE_TECLA}
          >
            {tecla === TECLA_APAGAR ? <Delete className="mx-auto h-6 w-6" aria-hidden="true" /> : tecla}
          </button>
        ))}
      </div>
      <button
        type="button"
        aria-label={rotuloAria(TECLA_LIMPAR)}
        disabled={desabilitado}
        onMouseDown={(evento) => evento.preventDefault()}
        onClick={() => onTecla(TECLA_LIMPAR)}
        className={`${CLASSE_TECLA} w-full text-base`}
      >
        Limpar
      </button>
    </div>
  );
}
